import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { maskName } from '../../../lib/locationUtils';

interface Property {
  id: string;
  title: string;
  user_id?: string;
  landlord_name?: string;
  pet_allowed?: string;
  location?: string;
  address?: string;
  price?: number;
  area?: number;
  rooms?: number;
  bathrooms?: number;
  furniture?: string | null;
  water_bill?: string | null;
  electric_bill?: string | null;
  description?: string | null;
  property_type?: string | null;
}

interface AiTenantScreeningModalProps {
  property: Property;
  onClose: () => void;
}

interface ChatMessage {
  id: string;
  role: 'ai' | 'user';
  text: string;
  typing?: boolean;
}

interface GeminiSummary {
  name: string;
  occupants: string;
  profession: string;
  petClause: string;
  extra: string;
  phone: string;
}

type ModalStep = 'chat' | 'confirm' | 'done';

const GEMINI_KEY = import.meta.env.VITE_GEMINI_API_KEY as string | undefined;

function getSessionToken(): string {
  const key = 'tenant_session_token';
  let token = localStorage.getItem(key);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(key, token);
  }
  return token;
}

export default function AiTenantScreeningModal({ property, onClose }: AiTenantScreeningModalProps) {
  const { user } = useAuth();

  const historyRef = useRef<{ role: string; content: string }[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<ModalStep>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);

  // confirm step state
  const [pendingSummary, setPendingSummary] = useState<GeminiSummary | null>(null);
  const [confirmPhone, setConfirmPhone] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [visibleHeight, setVisibleHeight] = useState<number>(
    window.visualViewport ? window.visualViewport.height : window.innerHeight
  );

  useEffect(() => {
    const updateHeight = () => {
      const h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      setVisibleHeight(h);
    };
    window.visualViewport?.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('scroll', updateHeight);
    window.addEventListener('resize', updateHeight);
    updateHeight();
    return () => {
      window.visualViewport?.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const isCommercial = ['店面', '廠房', '辦公室'].includes(property.property_type || '');

  const greeting = isCommercial
    ? `您好！我是藍舍 Gemini AI 助理 🏢\n\n我將協助您完成「${property.title}」的洽談申請，並把您的需求整理後傳送給屋主 ${maskName(property.landlord_name || '')}。\n\n請問您怎麼稱呼呢？`
    : `您好！我是藍舍 Gemini AI 助理 🏠\n\n我將協助您完成「${property.title}」的看房申請，並把您的資訊整理後傳送給屋主 ${maskName(property.landlord_name || '')}。\n\n請問您怎麼稱呼呢？`;

  useEffect(() => {
    setMessages([{ id: crypto.randomUUID(), role: 'ai', text: greeting }]);
    historyRef.current = [{ role: 'model', content: greeting }];
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, [messages]);

  useEffect(() => {
    if (step === 'chat' && !isSending) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isSending, step]);

  const callEdgeFunction = async (userText: string) => {
    historyRef.current.push({ role: 'user', content: userText });

    setIsSending(true);
    const typingId = crypto.randomUUID();
    setMessages(prev => [...prev, { id: typingId, role: 'ai', text: '', typing: true }]);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);

    const resolve = (text: string) => {
      setMessages(prev => prev.map(m => m.id === typingId ? { ...m, text, typing: false } : m));
      setIsSending(false);
    };

    try {
      const { data, error } = await supabase.functions.invoke('tenant-ai-chat', {
        body: {
          history: historyRef.current,
          propertyInfo: {
            title: property.title,
            location: property.location,
            address: property.address || '',
            price: property.price,
            landlord_name: property.landlord_name,
            pet_allowed: property.pet_allowed,
            area: property.area,
            rooms: property.rooms,
            bathrooms: property.bathrooms,
            furniture: property.furniture || '',
            water_bill: property.water_bill || '',
            electric_bill: property.electric_bill || '',
            description: property.description || '',
            property_type: property.property_type || '住宅',
            floor_info: (property as Record<string, unknown>).floor_info || '',
          },
          apiKey: GEMINI_KEY || '',
        },
      });

      if (error) {
        console.error('Edge function error:', error);
        historyRef.current.pop();
        resolve('抱歉，AI 助理暫時無法回應，請稍後再試 😢');
        return;
      }

      const reply: string = data?.reply || '抱歉，請再試一次。';
      const isComplete: boolean = data?.isComplete === true;
      const summary: GeminiSummary | null = data?.summary || null;

      historyRef.current.push({ role: 'model', content: reply });
      resolve(reply);

      if (isComplete && summary) {
        // 不直接送出，改進入確認步驟
        setTimeout(() => {
          setPendingSummary(summary);
          setConfirmPhone(summary.phone || '');
          setStep('confirm');
        }, 800);
      }
    } catch (err) {
      console.error('Unexpected error calling edge function:', err);
      historyRef.current.pop();
      resolve('抱歉，連線發生問題，請再試一次 😢');
    }
  };

  const submitToLandlord = async (summary: GeminiSummary, finalPhone: string) => {
    const lines = [
      `📋 看房申請 — Gemini AI 助理彙整報告`,
      ``,
      `👤 稱呼：${summary.name}`,
      `🏠 入住人數：${summary.occupants}`,
      `💼 職業：${summary.profession}`,
    ];
    if (summary.petClause) lines.push(`🐾 寵物條款：${summary.petClause}`);
    if (summary.extra) lines.push(`❓ 想問屋主：${summary.extra}`);
    lines.push(`📱 聯絡電話：${finalPhone}`);
    lines.push(``, `⚠️ 本申請人已閱讀並同意平台聲明，本對話記錄已存入屋主後台。`);
    lines.push(`（此訊息由藍舍 Gemini AI 助理自動生成）`);

    const fullMessage = lines.join('\n');

    await supabase.from('messages').insert({
      property_id: property.id,
      landlord_id: property.user_id,
      sender_name: summary.name || '匿名租客',
      sender_phone: finalPhone || null,
      sender_email: null,
      message: fullMessage,
      property_title: property.title,
      is_read: false,
      sender_user_id: user?.id || null,
      session_token: getSessionToken(),
    });

    // 同時儲存完整 AI 對話記錄到 ai_chat_sessions
    const now = new Date();
    const formatTime = (offset: number) => {
      const d = new Date(now.getTime() - offset * 60000);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const chatMessages = historyRef.current.map((h, idx) => ({
      from: h.role === 'model' ? 'ai' : 'tenant',
      text: h.content,
      time: formatTime(Math.max(0, historyRef.current.length - 1 - idx)),
    }));

    supabase.from('ai_chat_sessions').insert({
      property_id: property.id,
      property_title: property.title,
      landlord_id: property.user_id,
      session_token: getSessionToken(),
      status: 'completed',
      tenant_name: summary.name || '匿名租客',
      tenant_initials: (summary.name || '匿')[0],
      collected_residents: summary.occupants || '—',
      collected_occupation: summary.profession || '—',
      collected_phone: finalPhone || '—',
      messages: chatMessages,
    }).then(() => {/* 非同步存入，失敗不影響主流程 */}).catch(() => {});

    if (property.user_id) {
      supabase.functions.invoke('send-line-notification', {
        body: {
          landlord_id: property.user_id,
          sender_name: summary.name || '匿名租客',
          sender_phone: finalPhone || null,
          sender_email: null,
          message: fullMessage,
          property_title: property.title,
          property_id: property.id,
        },
      }).catch(() => {/* LINE 通知失敗不影響主流程 */});
    }
  };

  const handleConfirmSubmit = async () => {
    if (!pendingSummary || !confirmPhone.trim() || !agreed) return;
    setIsSubmitting(true);
    await submitToLandlord(pendingSummary, confirmPhone.trim());
    setIsSubmitting(false);
    setStep('done');
  };

  const handleSend = () => {
    const val = inputValue.trim();
    if (!val || isSending || step !== 'chat') return;
    setInputValue('');
    setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'user', text: val }]);
    callEdgeFunction(val);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="w-full sm:max-w-lg bg-white sm:rounded-3xl rounded-t-3xl overflow-hidden flex flex-col shadow-2xl"
        style={{ height: Math.min(700, visibleHeight - 8) }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-teal-700 to-teal-500 flex-shrink-0">
          <div className="relative w-10 h-10 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
            <i className="ri-robot-2-line text-white text-xl"></i>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-teal-600 animate-pulse"></span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white text-sm leading-tight">藍舍 Gemini AI 助理</div>
            <div className="text-teal-100 text-xs flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full inline-block bg-green-400 animate-pulse"></span>
              Gemini 2.0 Flash · 智能看房篩選
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 text-white transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Property info bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-50 to-white border-b border-teal-100 flex-shrink-0">
          <i className={`${isCommercial ? 'ri-store-2-line' : 'ri-home-4-line'} text-teal-500 text-sm flex-shrink-0`}></i>
          <span className="text-xs text-teal-700 font-medium truncate flex-1">{property.title}</span>
          <span className="text-xs text-teal-500 whitespace-nowrap flex-shrink-0 flex items-center gap-1">
            <i className="ri-user-line text-xs"></i>
            {maskName(property.landlord_name || '')}
          </span>
        </div>

        {/* ── chat step ── */}
        {step === 'chat' && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-stone-50/80">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {msg.role === 'ai' && (
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex-shrink-0 mt-0.5 shadow-sm">
                      <i className="ri-robot-2-line text-white text-sm"></i>
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-line shadow-sm ${
                      msg.role === 'ai'
                        ? 'bg-white text-gray-800 rounded-tl-sm'
                        : 'bg-teal-600 text-white rounded-tr-sm'
                    }`}
                  >
                    {msg.typing ? (
                      <div className="flex items-center gap-1.5 py-1 px-1">
                        <span className="w-2 h-2 bg-teal-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '160ms' }}></span>
                        <span className="w-2 h-2 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '320ms' }}></span>
                      </div>
                    ) : msg.text}
                  </div>
                </div>
              ))}
              <div ref={bottomRef}></div>
            </div>

            <div className="flex items-center gap-2 px-4 py-3 bg-white border-t border-stone-100 flex-shrink-0">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isSending ? 'Gemini 正在思考中...' : '輸入訊息，按 Enter 送出'}
                className="flex-1 px-4 py-2.5 bg-stone-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 transition-all placeholder-gray-400"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isSending}
                className="w-9 h-9 flex items-center justify-center bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 active:scale-95"
              >
                {isSending
                  ? <i className="ri-loader-4-line animate-spin text-sm"></i>
                  : <i className="ri-send-plane-fill text-sm"></i>}
              </button>
            </div>

            <div className="px-4 py-2 bg-white flex-shrink-0 border-t border-stone-50">
              <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1">
                <i className="ri-lock-line text-xs"></i>
                您的資訊僅供屋主聯繫使用，由 Gemini AI 安全處理
              </p>
            </div>
          </>
        )}

        {/* ── confirm step ── */}
        {step === 'confirm' && pendingSummary && (
          <div className="flex-1 overflow-y-auto flex flex-col">
            {/* 進度提示 */}
            <div className="flex items-center gap-2 px-5 py-3 bg-teal-50 border-b border-teal-100 flex-shrink-0">
              <i className="ri-file-list-3-line text-teal-600 text-base"></i>
              <p className="text-xs text-teal-700 font-medium">AI 已彙整您的資料，請確認後同意送出</p>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* 彙整摘要卡片 */}
              <div className="bg-stone-50 rounded-2xl px-4 py-4 space-y-2.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">AI 彙整資料</p>
                <div className="flex items-start gap-2.5">
                  <span className="text-sm mt-0.5">👤</span>
                  <div>
                    <p className="text-xs text-gray-400">稱呼</p>
                    <p className="text-sm text-gray-800 font-medium">{pendingSummary.name || '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-sm mt-0.5">🏠</span>
                  <div>
                    <p className="text-xs text-gray-400">入住人數</p>
                    <p className="text-sm text-gray-800 font-medium">{pendingSummary.occupants || '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="text-sm mt-0.5">💼</span>
                  <div>
                    <p className="text-xs text-gray-400">職業</p>
                    <p className="text-sm text-gray-800 font-medium">{pendingSummary.profession || '—'}</p>
                  </div>
                </div>
                {pendingSummary.petClause && (
                  <div className="flex items-start gap-2.5">
                    <span className="text-sm mt-0.5">🐾</span>
                    <div>
                      <p className="text-xs text-gray-400">寵物說明</p>
                      <p className="text-sm text-gray-800 font-medium">{pendingSummary.petClause}</p>
                    </div>
                  </div>
                )}
                {pendingSummary.extra && (
                  <div className="flex items-start gap-2.5">
                    <span className="text-sm mt-0.5">❓</span>
                    <div>
                      <p className="text-xs text-gray-400">想問屋主</p>
                      <p className="text-sm text-gray-800 font-medium">{pendingSummary.extra}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 電話欄位 */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  聯絡電話 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="tel"
                    value={confirmPhone}
                    onChange={(e) => setConfirmPhone(e.target.value)}
                    placeholder="請輸入您的聯絡電話"
                    className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">屋主將以此號碼與您確認看房時間</p>
              </div>

              {/* 聲明警示 */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-shield-keyhole-line text-amber-600 text-base"></i>
                  <p className="text-xs font-bold text-amber-800">平台使用聲明</p>
                </div>

                <div className="flex items-start gap-2.5">
                  <i className="ri-record-circle-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    本次對話<strong>全程記錄於屋主後台</strong>，作為雙方往來之正式存憑，送出後不可撤回。
                  </p>
                </div>

                <div className="flex items-start gap-2.5">
                  <i className="ri-error-warning-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    如有<strong>謊報或不實資訊</strong>，屋主均保有完整對話記錄作為法律依據，並保留追究責任之權利。
                  </p>
                </div>

                <div className="flex items-start gap-2.5">
                  <i className="ri-forbid-2-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong>謝絕仲介</strong>，嚴禁利用本平台侵犯屋主個人資料。屋主保留訴諸法律的一切權利。
                  </p>
                </div>
              </div>

              {/* 同意勾選 */}
              <label className="flex items-start gap-3 cursor-pointer select-none group">
                <div
                  className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${
                    agreed
                      ? 'bg-teal-600 border-teal-600'
                      : 'border-gray-300 group-hover:border-teal-400'
                  }`}
                  onClick={() => setAgreed(!agreed)}
                >
                  {agreed && <i className="ri-check-line text-white text-xs"></i>}
                </div>
                <p className="text-xs text-gray-600 leading-relaxed" onClick={() => setAgreed(!agreed)}>
                  我已閱讀並了解上述聲明，確認所提供之資訊均屬真實，並同意本對話記錄存入屋主後台。
                </p>
              </label>
            </div>

            {/* 送出按鈕 */}
            <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-gray-100">
              <button
                onClick={handleConfirmSubmit}
                disabled={!confirmPhone.trim() || !agreed || isSubmitting}
                className="w-full py-3.5 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm whitespace-nowrap active:scale-95"
              >
                {isSubmitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i>傳送中...</>
                ) : (
                  <><i className="ri-check-double-line"></i>同意並送出申請</>
                )}
              </button>
              <p className="text-center text-xs text-gray-400 mt-2">點擊送出即視為同意上述平台聲明</p>
            </div>
          </div>
        )}

        {/* ── done step ── */}
        {step === 'done' && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-4 bg-stone-50">
            <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-teal-500 text-4xl"></i>
            </div>
            <div className="text-center space-y-1.5">
              <p className="text-base font-bold text-gray-800">看房申請已成功送出！</p>
              <p className="text-sm text-gray-500">屋主將於近期與您聯絡確認看房時間</p>
            </div>
            <div className="w-full bg-teal-50 border border-teal-100 rounded-2xl px-4 py-3 flex items-start gap-2.5 mt-2">
              <i className="ri-information-line text-teal-500 text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-teal-700 leading-relaxed">
                本次對話記錄已存入屋主後台，如有任何問題請透過平台訊息與屋主聯繫。
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full py-3 bg-teal-600 text-white rounded-full font-medium hover:bg-teal-700 transition-colors cursor-pointer text-sm whitespace-nowrap mt-2 active:scale-95"
            >
              完成，關閉視窗
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
